import type {
  AvailableDecorationMethod,
  Product,
  ProductPosition,
} from '@/api/products';

export interface DirectSaleMethod {
  id: string;
  code: string;
  name: string;
}

/**
 * Whether Direct Sale can sell the method: active, and priced. The
 * catalogue names `directSalePrice` per position and method; null means the
 * quote would refuse it (no DIRECT_SALE price). An answer without the
 * field (an API from before it) is not filtered.
 */
function isSellable(method: AvailableDecorationMethod): boolean {
  return method.isActive !== false && method.directSalePrice !== null;
}

/**
 * The decoration methods the customer can pick for the product: those every
 * decorated position offers and Direct Sale can sell there (one method covers the whole product, so a
 * position that cannot do it rules it out), in the order of the first
 * position's list — default first. Before anything is decorated, the
 * methods of every position that is sold at all count.
 *
 * Fewer than two methods means there is nothing to choose; the caller then
 * shows no picker and the default applies, as before this choice existed.
 */
export function methodsForProduct(
  product: Pick<Product, 'positions'> | null | undefined,
  decoratedPositionIds: readonly string[]
): DirectSaleMethod[] {
  const positions = (product?.positions ?? []).filter((position) =>
    decoratedPositionIds.length > 0
      ? decoratedPositionIds.includes(position.id)
      : position.printSurcharge != null
  );
  if (positions.length === 0) return [];

  const offered = positions.map(
    (position) =>
      new Map(
        (position.availableDecorationMethods ?? [])
          .filter(isSellable)
          .map((method) => [method.id, method])
      )
  );
  const [first, ...rest] = offered;
  if (!first) return [];
  return Array.from(first.values())
    .filter((method) => rest.every((others) => others.has(method.id)))
    .map((method) => ({
      id: method.id,
      code: method.code,
      name: method.name ?? method.code,
    }));
}

/**
 * The method to send with the quote: the customer's pick while it is still
 * offered, otherwise none (every position keeps its default). A stale pick
 * — the design moved to a position that cannot do it — must never reach the
 * quote, which would refuse it.
 */
export function effectiveMethodId(
  methods: readonly DirectSaleMethod[],
  pickedId: string | null
): string | null {
  return methods.length > 1 && methods.some((m) => m.id === pickedId)
    ? pickedId
    : null;
}

/**
 * What decorating the position costs with the method: the method's
 * Direct Sale price there when the method applies to it, otherwise the
 * position's own (default method) surcharge. Null when not sold at all.
 */
export function positionPrice(
  position: Pick<
    ProductPosition,
    'printSurcharge' | 'availableDecorationMethods'
  >,
  methodId: string | null
): string | null {
  const method = methodId
    ? position.availableDecorationMethods?.find((m) => m.id === methodId)
    : undefined;
  if (method?.directSalePrice != null) return method.directSalePrice;
  return position.printSurcharge ?? null;
}

/**
 * The name of the method the position is decorated with: the chosen one
 * when it applies to the position, else the position's default.
 */
export function positionMethodName(
  position: Pick<
    ProductPosition,
    'decorationMethods' | 'availableDecorationMethods'
  >,
  methodId: string | null
): string | null {
  const method = methodId
    ? position.availableDecorationMethods?.find((m) => m.id === methodId)
    : undefined;
  return (
    method?.name ??
    position.decorationMethods?.name ??
    method?.code ??
    position.decorationMethods?.code ??
    null
  );
}

/**
 * The product as the editor core must see it: every position that offers
 * the chosen method carries it as its method, so the embroidery rule, the
 * thread limits and the mockup layering follow the customer's choice.
 * Without a choice the product is returned unchanged (each default).
 * Direct Sale only — the Seller Creator never goes through this.
 */
export function withEffectiveMethod(
  product: Product,
  methodId: string | null
): Product {
  if (!methodId) return product;
  return {
    ...product,
    positions: product.positions?.map((position) => {
      const method = position.availableDecorationMethods?.find(
        (m) => m.id === methodId
      );
      if (!method || position.decorationMethods?.id === methodId) {
        return position;
      }
      return {
        ...position,
        decorationMethods: {
          id: method.id,
          code: method.code,
          name: method.name ?? method.code,
          minDpi: method.minDpi ?? undefined,
          maxColors: method.maxColors ?? undefined,
          sortOrder: position.decorationMethods?.sortOrder ?? 0,
        },
      };
    }),
  };
}
